import React, { useEffect, useState } from 'react';
import {
  BookOpen,
  Users,
  Calendar,
  FileText,
  MapPin,
  Phone,
  Mail,
  Award,
  Lightbulb,
  Heart,
  Star,
  ArrowRight,
  Globe
} from 'lucide-react';

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const heroSlides = [
    {
      title: "Young Scholars",
      subtitle: "Where Children Come First",
      description: "At the Talented and Gifted School for Young Scholars, faculties, parents and students work together to prepare all students to excel in life and develop to their full potential, both intellectually and socially.",
      cta: "Learn More",
      ctaLink: "https://www.schools.nyc.gov/enrollment/enroll-grade-by-grade/gifted-talented"
    },
    {
      title: "Academic Excellence",
      subtitle: "Nurturing Gifted Minds",
      description: "Our dedicated faculty creates an environment where talented and gifted students thrive through challenging curriculum, innovative teaching methods, and personalized attention.",
      cta: "Learn More",
      ctaLink: "https://www.schools.nyc.gov/enrollment/enroll-grade-by-grade/gifted-talented"
    },
    {
      title: "Community Partnership",
      subtitle: "Building Tomorrow Together",
      description: "Join our vibrant community where parents, teachers, and students collaborate to create an exceptional educational experience that prepares scholars for future success.",
      cta: "Learn More",
      ctaLink: "https://www.schools.nyc.gov/enrollment/enroll-grade-by-grade/gifted-talented"
    }
  ];

  // Auto-rotation must be pausable (WCAG 2.2.2) and must not run for people who
  // asked for reduced motion. It also pauses while the hero is hovered or holds focus.
  const [paused, setPaused] = useState(() => typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const [hold, setHold] = useState(false);
  useEffect(() => {
    if (paused || hold) return undefined;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [heroSlides.length, paused, hold]);

  const quickAccessItems = [
    { icon: BookOpen, title: "Admissions", href: "/admissions", color: "primary" },
    { icon: Users, title: "Parent's Corner", href: "/resources/parents", color: "gold" },
    { icon: FileText, title: "TAG PTA", href: "https://www.tagscholarspta.org/", color: "primary", external: true },
  ];

  const achievements = [
    { number: "95%", label: "Student Success Rate", icon: Award },
    { number: "15:1", label: "Student-Teacher Ratio", icon: Users },
    { number: "25+", label: "Years of Excellence", icon: Star },
    { number: "100%", label: "College Acceptance", icon: Lightbulb },
  ];

  const programs = [
    {
      title: "STEM Innovation",
      description: "Cutting-edge science, technology, engineering, and mathematics programs.",
      image: "bg-primary-800",
      features: ["Robotics Lab", "Coding Classes", "Science Research"]
    },
    {
      title: "Arts & Creativity",
      description: "Comprehensive arts education fostering creative expression and cultural appreciation.",
      image: "bg-gold-600",
      features: ["Visual Arts", "Music Program", "Theater Arts"]
    },
    {
      title: "Global Citizenship",
      description: "Preparing students for an interconnected world through language and cultural studies.",
      image: "bg-primary-600",
      features: ["Language Immersion", "Cultural Exchange", "Global Projects"]
    }
  ];

  const upcomingItems = [
    {
      date: "Coming Soon",
      title: "Parent-Teacher Conferences",
      excerpt: "Schedule your conference time to discuss your child's progress and academic goals.",
      category: "Important",
      link: "/assets/docs/Spring 2026 PTC Sign-Up Genius Link.pdf"
    },
    {
      date: "February 24, 2026",
      title: "Student Bake Sale Fundraiser",
      excerpt: "Join us for our student-led bake sale fundraiser in Room 106 from 8:05 AM - 2:25 PM. Items range from $1-$5!",
      category: "Fundraiser",
      link: "/assets/docs/Student Bake Sale Fundraiser.pdf"
    }
  ];

  return (
    <main id="main-content" className="bg-white">
      {/* Hero Section — full-viewport video with a lower-third title band */}
      <section className="relative flex h-[100svh] min-h-[560px] w-full flex-col overflow-hidden bg-secondary-900">
        {/* Video Background */}
        <div className="absolute inset-0">
          <video
            autoPlay
            muted
            loop
            playsInline
            aria-hidden="true"
            className="w-full h-full object-cover"
          >
            <source src="/assets/videos/home/TAG Young Scholars.mp4" type="video/mp4" />
          </video>
          {/* Gentle top/bottom vignette only: no text sits on the footage any more. */}
          <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-b from-secondary-900/40 via-transparent to-secondary-900/40"></div>
        </div>

        {/* Lower-third band: the slide copy, its CTA and the slide controls sit
            on a slim, fully opaque secondary-900 strip pinned to the bottom of
            the hero, so the video stays unobstructed above it. The backing is
            solid on purpose: contrast over a <video> can't be measured, so
            every line here needs an opaque surface (white 17.85:1,
            gold-400 10.69:1). */}
        <div
          className="relative z-10 mt-auto w-full border-t-2 border-gold-400 bg-secondary-900"
          onMouseEnter={() => setHold(true)}
          onMouseLeave={() => setHold(false)}
          onFocus={() => setHold(true)}
          onBlur={() => setHold(false)}
        >
          <div className="mx-auto flex max-w-[1400px] flex-col gap-3 px-6 py-3.5 md:px-10 md:py-4 lg:flex-row lg:items-center lg:justify-between lg:gap-8">
            {/* Slide Content */}
            {/* Announce a slide only when the visitor changed it; an auto-rotating live region would interrupt a screen reader every six seconds. */}
            <div className="min-w-0 animate-fade-in motion-reduce:animate-none" key={currentSlide} aria-live={paused ? 'polite' : 'off'} aria-atomic="true">
              {/* Badge and description stay in the accessibility tree; visually
                  the band carries only the headline and one short line. */}
              <p className="sr-only">Talented and Gifted Education</p>

              {/* Two lines are reserved below sm, where the longest slide title
                  wraps, so the band keeps one height from slide to slide. */}
              <h1 className="min-h-[2.5em] text-xl font-extrabold leading-tight tracking-tight text-white sm:min-h-0 md:text-2xl xl:text-3xl">
                <span className="font-light">Nurturing</span>{' '}
                <span className="whitespace-nowrap">{heroSlides[currentSlide].title}</span>
              </h1>

              <h2 className="mt-0.5 text-xs font-semibold text-gold-400 md:text-sm">
                {heroSlides[currentSlide].subtitle}
              </h2>

              <p className="sr-only">
                {heroSlides[currentSlide].description}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 lg:shrink-0 lg:flex-nowrap">
              <a
                href={heroSlides[currentSlide].ctaLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-full bg-white px-3 py-2.5 text-xs font-bold uppercase text-center tracking-[0.06em] text-primary-900 transition-colors duration-300 hover:bg-neutral-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white sm:px-4 sm:tracking-[0.14em]"
                aria-label={`Learn more about ${heroSlides[currentSlide].title}`}
              >
                {heroSlides[currentSlide].cta}
              </a>

              {/* Slide indicators + pause control, on the same solid band */}
              <div className="flex items-center space-x-3">
                {heroSlides.map((_, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => { setCurrentSlide(index); setPaused(true); }}
                    aria-label={`Go to slide ${index + 1}`}
                    aria-current={currentSlide === index ? 'true' : undefined}
                    className="group flex h-6 items-center focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                  >
                    {/* 24px-tall hit area around the thin line (WCAG 2.5.8) */}
                    <span className={`block w-8 transition-colors duration-500 ${currentSlide === index ? 'h-1 bg-white' : 'h-0.5 bg-secondary-400 group-hover:bg-secondary-300'}`} />
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setPaused((p) => !p)}
                  aria-pressed={paused}
                  className="ml-2 max-w-full rounded-full border border-secondary-400 px-3 py-1.5 text-xs font-medium uppercase tracking-wide text-white hover:bg-secondary-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                >
                  {paused ? 'Play slides' : 'Pause slides'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Access Section */}
      <section className="section-padding bg-neutral-50">
        <div className="container-padding">
          <div className="text-center mb-20">
            <h2 className="text-3xl md:text-4xl font-light text-secondary-900 mb-6">Quick Access</h2>
            <div className="w-16 h-px bg-primary-500 mx-auto"></div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
            {quickAccessItems.map((item, index) => (
              <a
                key={index}
                href={item.href}
                {...(item.external && { target: "_blank", rel: "noopener noreferrer" })}
                className="group bg-white rounded-xl p-8 text-center card-hover shadow-sm border border-neutral-200 hover:border-primary-300"
              >
                <div className={`w-14 h-14 mx-auto mb-6 rounded-lg flex items-center justify-center ${
                  item.color === 'primary'
                    ? 'bg-primary-50 text-primary-700 group-hover:bg-primary-600 group-hover:text-white'
                    : 'bg-accent-50 text-accent-700 group-hover:bg-accent-500 group-hover:text-white'
                } transition-all duration-300`}>
                  <item.icon className="h-7 w-7" />
                </div>
                <h3 className="text-sm font-medium text-secondary-800 group-hover:text-primary-700 transition-colors">
                  {item.title}
                </h3>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="section-padding bg-white">
        <div className="container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div>
              <div className="inline-flex items-center space-x-3 bg-primary-50 text-primary-700 px-6 py-3 rounded-full mb-8">
                <Heart className="h-4 w-4" />
                <span className="text-sm font-medium tracking-wide uppercase">Our Mission</span>
              </div>

              <h2 className="text-4xl lg:text-5xl font-light text-secondary-900 mb-8 leading-tight">
                Nurturing Tomorrow's
                <span className="font-medium block text-primary-700">Innovators</span>
              </h2>

              <p className="text-lg text-secondary-700 leading-relaxed mb-12 font-light">
                At TAG School for Young Scholars, we believe every talented and gifted student has unlimited potential. Our dedicated educators create an environment where intellectual curiosity thrives, creativity flourishes, and academic excellence is achieved through challenging, personalized learning experiences.
              </p>

              <div className="grid grid-cols-2 gap-8 mb-12">
                {achievements.map((achievement, index) => (
                  <div key={index} className="text-center">
                    <div className="flex items-center justify-center w-14 h-14 bg-primary-50 rounded-lg mb-4 mx-auto border border-primary-100">
                      <achievement.icon className="h-6 w-6 text-primary-700" />
                    </div>
                    <div className="text-3xl font-light text-primary-900 mb-1">{achievement.number}</div>
                    <div className="text-sm text-primary-600 font-medium">{achievement.label}</div>
                  </div>
                ))}
              </div>

              <div className="flex gap-6">
                <a href="/about" className="btn-primary" aria-label="Learn more about TAG School's mission">Learn More</a>
                <a href="/coming-soon" className="btn-secondary">Contact Us</a>
              </div>
            </div>

            <div className="relative">
              <div className="grid grid-cols-2 gap-6 h-[500px]">
                <div className="space-y-6">
                  <div className="bg-primary-800 rounded-xl h-48 flex items-center justify-center text-white font-light shadow-lg">
                    Excellence in Learning
                  </div>
                  <div className="bg-accent-800 rounded-xl h-48 flex items-center justify-center text-white font-light shadow-lg">
                    Creative Expression
                  </div>
                </div>
                <div className="space-y-6 mt-8">
                  <div className="bg-cool-800 rounded-xl h-48 flex items-center justify-center text-white font-light shadow-lg">
                    Global Perspective
                  </div>
                  <div className="bg-secondary-800 rounded-xl h-48 flex items-center justify-center text-white font-light shadow-lg">
                    Future Ready
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Programs Section */}
      <section className="section-padding bg-neutral-50">
        <div className="container-padding">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-gold-100 text-gold-700 px-4 py-2 rounded-full mb-6">
              <Lightbulb className="h-4 w-4" />
              <span className="text-sm font-medium">Our Programs</span>
            </div>
            <h2 className="text-4xl font-light text-primary-900 mb-6">Exceptional Learning Opportunities</h2>
            <p className="text-lg text-primary-700 max-w-3xl mx-auto font-light">
              Discover our comprehensive programs designed to inspire, challenge, and prepare students for success in an ever-evolving world.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {programs.map((program, index) => (
              <div key={index} className="group bg-white rounded-2xl overflow-hidden card-hover shadow-lg">
                <div className={`h-48 ${program.image} flex items-center justify-center text-white text-xl font-bold`}>
                  {program.title}
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-medium text-primary-900 mb-3">{program.title}</h3>
                  <p className="text-primary-700 mb-4 font-light">{program.description}</p>
                  <ul className="space-y-2 mb-6">
                    {program.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center text-sm text-primary-700">
                        <Star aria-hidden="true" className="h-4 w-4 text-gold-600 mr-2" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <a
                    href="/coming-soon"
                    className="text-primary-600 hover:text-primary-700 font-medium flex items-center group-hover:translate-x-1 transition-transform"
                    aria-label={`Learn more about ${program.title}`}
                  >
                    Learn More <ArrowRight aria-hidden="true" className="h-4 w-4 ml-1" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Important Announcements Section */}
      <section className="section-padding bg-white">
        <div className="container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Important Announcements */}
            <div className="lg:col-span-2">
              <div className="flex items-center mb-8">
                <FileText className="h-6 w-6 text-primary-600 mr-3" />
                <h2 className="text-3xl font-light text-primary-900">Important Announcements</h2>
              </div>

              <div className="space-y-6">
                {upcomingItems.map((item, index) => (
                  <article key={index} className="bg-slate-50 rounded-2xl p-6 hover:shadow-lg transition-shadow">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm text-primary-700 font-medium">{item.date}</span>
                      <span className="bg-gold-100 text-gold-700 px-3 py-1 rounded-full text-sm font-medium">
                        {item.category}
                      </span>
                    </div>
                    <h3 className="text-xl font-medium text-primary-900 mb-2">{item.title}</h3>
                    <p className="text-primary-700 mb-4 font-light">{item.excerpt}</p>
                    <a
                      href={item.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-700 hover:text-primary-800 font-medium flex items-center"
                      aria-label={`Learn more about ${item.title} (opens PDF in a new tab)`}
                    >
                      Learn More (PDF) <ArrowRight aria-hidden="true" className="h-4 w-4 ml-1" />
                    </a>
                  </article>
                ))}
              </div>
            </div>

            {/* Sidebar */}
            <aside className="space-y-8" aria-label="Upcoming events and contact information">
              {/* Calendar Widget */}
              <div className="bg-primary-700 rounded-2xl p-6 text-white shadow-xl">
                <h3 className="text-xl font-bold mb-4 flex items-center">
                  <Calendar className="h-5 w-5 mr-2" />
                  Upcoming Events
                </h3>
                <div className="space-y-3">
                  <div className="bg-white/20 rounded-lg p-3">
                    <div className="text-sm font-medium">Spring Concert</div>
                    <div className="text-xs">March 25, 2024</div>
                  </div>
                  <div className="bg-white/20 rounded-lg p-3">
                    <div className="text-sm font-medium">Science Fair</div>
                    <div className="text-xs">April 2, 2024</div>
                  </div>
                  <div className="bg-white/20 rounded-lg p-3">
                    <div className="text-sm font-medium">Open House</div>
                    <div className="text-xs">April 10, 2024</div>
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div className="bg-slate-50 rounded-2xl p-6">
                <h3 className="text-xl font-medium text-primary-900 mb-6">Get in Touch</h3>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <MapPin aria-hidden="true" className="h-5 w-5 text-gold-700 mr-3" />
                    <span className="text-primary-700">240 East 109 Street, Manhattan, NY 10029</span>
                  </div>
                  <div className="flex items-start">
                    <Phone aria-hidden="true" className="h-5 w-5 text-gold-700 mr-3 mt-0.5" />
                    <div className="text-primary-700">
                      <div>Phone: 212-860-6003</div>
                      <div>Fax: 212-831-1842</div>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Mail aria-hidden="true" className="h-5 w-5 text-gold-700 mr-3" />
                    <span className="text-primary-700">info@schoolname.edu</span>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      {/* Flat, not a gradient: was fading white -> neutral-100 -> secondary-900
          (dark navy) with dark text on top, which risked unreadable
          dark-on-dark text near the bottom and, even after that endpoint was
          lightened, still left axe unable to resolve a single background
          color for the text sitting on it. */}
      <section className="section-padding bg-neutral-100 text-secondary-900">
        <div className="container-padding text-center">
          <div className="max-w-3xl mx-auto">
            <Globe className="h-16 w-16 mx-auto mb-6 text-primary-600" />
            <h2 className="text-4xl font-light mb-6 text-secondary-800">Ready to Join Our Community?</h2>
            <p className="text-xl text-secondary-700 mb-8 font-light">
              Discover how TAG School for Young Scholars can provide your child with an exceptional educational experience that prepares them for a bright future.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <a href="/coming-soon" className="btn-primary">
                Schedule a Visit
              </a>
              <a
                href="/coming-soon"
                className="bg-transparent border-2 border-secondary-400 hover:border-secondary-600 text-secondary-700 hover:text-secondary-900 font-medium px-8 py-4 rounded-lg transition-all duration-300 hover:bg-secondary-50"
              >
                Download Brochure
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Home;